import React, { useContext, useEffect } from 'react';
import styles from './PlayerList.module.scss';
import { PropertyType } from 'esa-ui-library/lib/types';
import { Table, Pagination } from 'esa-ui-library';
import { find } from 'lodash';
import { flattenAll, playerPages, PLAYERS_SEARCH_TYPES, useStore } from '../../../common';
import {
	Header,
	PageAuth,
	PageContainer,
	Sidebar,
	SideMenu,
	Page,
	PageHeader,
	GlobalAlert,
	Filters,
} from '../../components';
import { PERMISSIONS } from '../../constants';
import { AuthContext } from '../../../logic';
import { getRows } from './RowProvider';
import { SearchFilter, PartnersFilter, VipLevelsFilter } from '../../components/Filters/components';
import { TableActions } from '../../components/TableActions/TableActions';
import { tableActions } from '../../components/TableActions/TableActions.const';
import { playersListMap } from './PlayerList.const';
import { getPlayersExport } from '../../../api';
import { filterPlayerFieldsByEnv, filterSearchOptionsByEnv, getFiltersUrl } from './PlayerList.helper';
import { getColumns, getInitialColumns } from '../../../common/services/getColumns';

const PlayerList = (): JSX.Element => {
	const [state, dispatch] = useStore();
	const { players, rows, columns, pagesTotal, currentPage, playersLoading, filters, perPage } = state;
	const { token, user } = useContext(AuthContext);
	const filteredPlayersFields = filterPlayerFieldsByEnv(playersListMap);
	const isItaly = process.env.REACT_APP_SITE_TITLE?.includes('Italy');

	const setColumnsConfig = (columns: []) => {
		return columns.map((c) => {
			const fieldPosition = find(playersListMap, ['label', c])?.position;
			return {
				label: c,
				cellClassName: fieldPosition ? styles[fieldPosition] : '',
			};
		});
	};

	useEffect(() => {
		dispatch('LOADING', true);
		dispatch('PARTNERS_INIT', user);
		dispatch('PLAYERS_INIT', { token, page: currentPage, perPage });
		dispatch('FILTERS_INIT', { token, filters: ['player_levels'] });
		dispatch('INIT_COLUMNS', {
			columns: getColumns('playersList', filteredPlayersFields),
			initialColumns: getInitialColumns(filteredPlayersFields),
		});
	}, []);

	useEffect(() => {
		if (columns.length) {
			const mappedPlayers = players.map((player: PropertyType) => {
				const partners = user?.partners && flattenAll(user.partners);

				const skin = find(partners, ['skinId', player.skin_id]);
				const partner = find(partners, ['partnerId', skin.partnerId]);

				return { ...player, partner_skin: `${partner?.label} - ${skin?.label}` };
			});
			dispatch('UPDATE_ROWS', getRows(mappedPlayers, columns));
		}
	}, [players, columns]);

	const onPageChange = (page: number | string) => {
		dispatch('LOADING', true);
		dispatch('PAGE_UPDATE', { token, page, perPage });
	};

	const onPerPageChange = (value: PropertyType) => {
		const perPage = value[0].value;
		dispatch('LOADING', true);
		dispatch('PER_PAGE', perPage);
		dispatch('PAGE_UPDATE', { token, currentPage, perPage });
	};

	return (
		<>
			<Header dataId='header' />
			<PageAuth permission={PERMISSIONS.canViewOwnDetails}>
				<PageContainer>
					<Sidebar dataId='admin-sidebar'>
						<SideMenu options={playerPages} title='Players' />
					</Sidebar>
					<Page>
						<PageHeader title='Player List' />
						<section className={styles.PageContent}>
							<Filters>
								<PartnersFilter />
								{isItaly && <VipLevelsFilter />}
								<div style={{ marginLeft: 'auto' }}>
									<SearchFilter searchTypes={filterSearchOptionsByEnv(PLAYERS_SEARCH_TYPES)} />
								</div>
							</Filters>
							<>
								{rows.length > 0 && (
									<TableActions
										exportData={{
											api: getPlayersExport,
											csvHeaders: filteredPlayersFields,
											filtersQuery: getFiltersUrl(filters),
										}}
										filters={[tableActions.EXPORT, tableActions.COLUMNS]}
									/>
								)}
								<Table
									titles={setColumnsConfig(columns)}
									data={rows}
									className={styles.PlayersTable}
									rowClassName={styles.PlayerRow}
									isLoading={playersLoading}
								/>
								{!!players.length && (
									<Pagination
										showPerPage
										onPerPage={onPerPageChange}
										perPage={perPage}
										total={Math.ceil(pagesTotal / +perPage)}
										current={currentPage}
										onChange={onPageChange}
									/>
								)}
							</>
						</section>
					</Page>
				</PageContainer>
				<GlobalAlert />
			</PageAuth>
		</>
	);
};

export { PlayerList };
